import { Button, Icon, Modal, Spinner } from '@aidkitorg/component-library';
import { PublishStep } from '@aidkitorg/types/lib/distroFileNames';

export const PUBLISH_PROGRESS_POLLING_INTERVAL_MS = 1000;

type Props = {
  open: boolean;
  steps: PublishStep[];
  currentStep: PublishStep | null;
  done: boolean;
  error: string | null;
  isPublishing: boolean;
  onClose: () => void;
};

const SYNCING_FRONTEND_LABEL = 'Syncing frontend';

export function PublishProgressModal({ open, steps, currentStep, done, error, isPublishing, onClose }: Props) {
  // currentStep = last completed step (null = nothing done yet)
  // Steps up to and including currentStep are done; the step after is spinning
  const currentStepIndex = currentStep !== null ? steps.indexOf(currentStep) : -1;
  const completedSteps = new Set(
    currentStepIndex !== -1 ? steps.slice(0, currentStepIndex + 1) : []
  );
  const spinningStep = currentStepIndex !== -1
    ? steps[currentStepIndex + 1] ?? null
    : steps[0] ?? null;

  type StepEntry = { label: string; isDone: boolean; isCurrent: boolean; isError: boolean };
  const allSteps: StepEntry[] = [
    ...steps.map((label) => ({
      label,
      isDone: done || completedSteps.has(label),
      isCurrent: !done && label === spinningStep,
      isError: !!error && label === spinningStep,
    })),
    {
      label: SYNCING_FRONTEND_LABEL,
      isDone: done && !error,
      isCurrent: !error && !done && spinningStep === null && currentStep !== null,
      isError: false,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      preventBackdropClick={isPublishing}
      title="Publishing Survey"
      size="sm"
    >
      <div>
        <ul className="mb-4 space-y-2">
          {allSteps.map(({ label, isDone, isCurrent, isError }) => (
            <li key={label} className="flex items-center gap-3 text-sm">
              <span className="w-5 flex-shrink-0 flex items-center justify-center">
                {isError ? (
                  <Icon name="XMarkIcon" type="solid" className="w-5 h-5 text-red-500" role="informative" title="Error" />
                ) : isDone ? (
                  <Icon name="CheckCircleIcon" type="solid" className="w-5 h-5 text-green-500" role="informative" title="Complete" />
                ) : isCurrent ? (
                  <Spinner size="xsmall" />
                ) : (
                  <span className="text-gray-300 font-bold">–</span>
                )}
              </span>
              <span className={isDone ? 'text-gray-700' : isCurrent ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                {label}
              </span>
            </li>
          ))}
        </ul>

        {done && (
          <p className="text-sm text-green-700 font-medium mb-4">Survey published successfully.</p>
        )}

        {error && (
          <p className="text-sm text-red-600 mb-4">{error}</p>
        )}

        {(done || error) && (
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose} autoFocus={done}>
              {done ? 'Done' : 'Close'}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
